// Angular
import { Observable, map, startWith, takeUntil } from 'rxjs';
import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

// Libraries
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { TranslateService } from '@ngx-translate/core';

// Services
import { UnitConfigService } from '../../services/unit-config.service';
import { CoreService } from '../../../../shared/services';
import { ChannelService } from '../../../channel/services';

// Models
import { UnitConfigViewModel, ChannelViewModel, TotalizerViewModel } from '../../models/unit-config-view.model';
import { Channel } from '../../../channel/models';
import { UnitOfMeasureModel } from '../../../unit-of-measure/models';
import { AppCleanup } from '../../../../shared/models';

// Helpers
import { FormHelper } from '../../../../shared/helpers';
import { UtilityHelper } from '../../../../shared/helpers';
import { ModbusHelper } from '../../helpers/modbus.helper';

// Components
import { ChannelExpressionDialogComponent } from '../channel-expression-dialog/channel-expression-dialog.component';

// Enums
import { StatusType, UnitConfigType } from '../../../../shared/enums';

@Component({
	selector: 'unit-config-channel-add-dialog',
	templateUrl: './unit-config-channel-add-dialog.component.html',
	styleUrls: ['./unit-config-channel-add-dialog.component.scss'],
})
export class UnitConfigChannelAddDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	unitConfig: UnitConfigViewModel;
	channel: ChannelViewModel;
	channels: Channel[];
	unitConfigChannels: ChannelViewModel[];
	filteredChannels: Observable<Channel[]>;
	registers: string;
	originalId: number;
	form: FormGroup;
	isEdit: boolean = false;
	idCopy: string;
	channelKind: ChannelKindType;
	channelKinds = [
		{
			name: 'Direct',
			value: 1,
		},
		{
			name: 'Derived',
			value: 2,
		},
		{
			name: 'Totalizer',
			value: 3,
		},
	];
	unitsOfMeasure: string[] = [];
	typeTypes: string[] = ['float', 'int16', 'int32'];
	isCopy: boolean = false;
	totalizerBaseArray = [
		{
			name: 'Second',
			value: 1,
		},
		{
			name: 'Minute',
			value: 2,
		},
		{
			name: 'Hour',
			value: 3,
		},
		{
			name: 'Day',
			value: 4,
		},
	];

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: {
			isCopy: boolean;
			unitConfig: UnitConfigViewModel;
			channel: ChannelViewModel;
			channels: ChannelViewModel[];
			unitsOfMeasure: UnitOfMeasureModel[];
			registers: string;
		},
		private dialogRef: MatDialogRef<UnitConfigChannelAddDialogComponent>,
		private formBuilder: FormBuilder,
		private unitConfigService: UnitConfigService,
		private coreService: CoreService,
		public dialog: MatDialog,
		private translateService: TranslateService,
		private channelService: ChannelService
	) {
		super();
	}

	ngOnInit() {
		this.channelService.channelsReady$.pipe(takeUntil(this.destroy)).subscribe((channels) => {
			this.channels = channels;
		});

		if (this.data?.unitConfig) {
			this.unitConfig = this.data.unitConfig;
			this.unitConfigChannels = this.unitConfig.channels.filter((channel) => channel.kind === ChannelKindType.DIRECT);
		}

		if (this.data?.registers) {
			this.registers = this.data.registers;
		}

		if (this.data?.unitsOfMeasure) {
			this.data.unitsOfMeasure.forEach((uom) => {
				this.unitsOfMeasure.push(uom.name);
			});
		}

		if (this.data?.channel) {
			// If edit
			this.isEdit = true;
			this.channel = this.data.channel;
			this.idCopy = UtilityHelper.deepCopy(this.channel.id);

			if (this.channel.type === 'integer') {
				this.channel.type = 'int32';
			}

			this.channelKind = this.channel.kind;
			this.originalId = UtilityHelper.deepCopy(this.channel.id);
		}

		if (this.data?.isCopy) {
			// If copy
			this.isCopy = true;
			this.isEdit = false;
		}

		this.form = this.formBuilder.group({
			type: [this.isEdit || this.isCopy ? this.channel.type : null, [Validators.required]],
			id: [{ value: this.isEdit ? this.channel.id : null, disabled: !this.channel?.type }, [Validators.required, Validators.maxLength(30), this.idValidator.bind(this)]],
			name: [this.isEdit || this.isCopy ? this.channel.name : null, [Validators.required, Validators.maxLength(30), this.channelValidator.bind(this)]],
			description: [this.isEdit || this.isCopy ? this.channel.description : null, Validators.maxLength(100)],
			kind: [this.isEdit || this.isCopy ? this.channel.kind : 1, Validators.maxLength(100)],
			enabled: [this.isEdit || this.isCopy ? this.channel.enabled : true],
			gain: [this.isEdit || this.isCopy ? this.channel.gain : 0, Validators.maxLength(10)],
			min: [this.isEdit || this.isCopy ? this.channel.min : 0, Validators.maxLength(10)],
			max: [this.isEdit || this.isCopy ? this.channel.max : 0, Validators.maxLength(10)],
			offset: [this.isEdit || this.isCopy ? this.channel.offset : 0, Validators.maxLength(10)],
			transform: [this.isEdit || this.isCopy ? this.channel.transform : ''],
			channelSourceName: [(this.isEdit || this.isCopy) && this.channelKind === ChannelKindType.TOTALIZER ? this.channel.totalizer.channelSourceName : ''],
			totalizerBase: [(this.isEdit || this.isCopy) && this.channelKind === ChannelKindType.TOTALIZER ? this.channel.totalizer.totalizerBase : ''],
			totalizerCalcFactor: [(this.isEdit || this.isCopy) && this.channelKind === ChannelKindType.TOTALIZER ? this.channel.totalizer.totalizerCalcFactor : ''],
			uom: [this.isEdit || this.isCopy ? this.channel.uom : null, [Validators.required]],
		});

		this.filteredChannels = this.form.controls.name.valueChanges.pipe(
			startWith(''),
			map((value) => this.filterChannels(value || ''))
		);

		if (this.isViewer && this.isEdit) {
			setTimeout(() => {
				this.form?.controls['uom'].disable();
			}, 1);
		}
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	filterChannels(value: string): Channel[] {
		const filterValue = value.toLowerCase();
		return this.channels.filter((channel) => channel.label.toLowerCase().includes(filterValue));
	}

	filterUoms(value: string): string[] {
		const filterValue = value.trim().toUpperCase();
		return this.unitsOfMeasure.filter((uom) => uom.toUpperCase().includes(filterValue));
	}

	channelValidator(control: FormControl) {
		if (control.value && this.form && this.unitConfig.channels) {
			// Adding "this.form", because when editing, the validator runs before the form is ready
			let nameExists = this.unitConfig.channels.find((item) => item.name.toLowerCase() === control.value.toLowerCase());

			if (nameExists) {
				if (this.isEdit) {
					if (nameExists.id !== this.idCopy) {
						return { nameExists: true }; // Check if name already exists
					} else {
						return null;
					}
				}

				return { nameExists: true }; // Check if name already exists
			}

			return null;
		}
	}

	openExpressionDialog(event) {
		event.preventDefault();
		const controls = this.form.controls;
		const dialogRef = this.dialog.open(ChannelExpressionDialogComponent, {
			data: {
				expression: controls.transform?.value ? controls.transform?.value : null,
				currentChannel: this.channel,
				channels: this.unitConfig.channels,
			},
			width: '100%',
			maxWidth: '500px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((data: string) => {
				if (!data) {
					return;
				}

				this.form.patchValue({
					transform: data,
				});

				this.form.controls.kind.markAsDirty();
			});
	}

	enableId() {
		if (this.form?.controls['type'].value) {
			this.form?.controls['id'].enable();
		} else {
			this.form?.controls['id'].disable();
		}
	}

	idValidator(control: FormControl) {
		if (this.unitConfig.type !== UnitConfigType.MODBUS) {
			return null;
		}

		if (control.value && this.form) {
			// Adding "this.form", because when editing, the validator runs before the form is ready
			if (control.value === '--') {
				return null;
			}

			let id = Number(control.value);

			if (isNaN(id)) {
				return { notNumber: true }; // Check for type
			}

			let float = this.form.controls['type'].value === 'float'; // If the id type is fload (it takes two consecutive ids)
			let isInRange = false;
			let isOverlapping = false;
			let rangeArray = [];
			let tempArray = this.registers.split(';');
			let localChannels = UtilityHelper.deepCopy(this.unitConfig.channels);

			if (this.originalId) {
				let index = localChannels.findIndex((item) => item.id === this.originalId);
				localChannels.splice(index, 1);
			}

			tempArray.forEach((item) => {
				rangeArray.push(item.split(':'));
			});

			isInRange = this.isInRange(id, rangeArray, float ? true : false);

			if (localChannels && localChannels.length > 0) {
				isOverlapping = this.isOverlapping(id, localChannels, float ? true : false);
			}

			if (isInRange) {
				if (!isOverlapping) {
					return null; // Everything checks out
				} else {
					return { isOverlapping: true }; // Id overlaps
				}
			} else {
				if (!isOverlapping) {
					return { notInRange: true }; // Id is not in range
				} else {
					return { notInRange: true, isOverlapping: true }; // Id is not in range and overlaps
				}
			}
		}
	}

	isInRange(id, rangeArray, float) {
		// Checks if id is whithin a range
		for (let i = 0; i < rangeArray.length; i++) {
			let min = Number(rangeArray[i][0]); // Range start
			let max = Number(rangeArray[i][0]) + Number(rangeArray[i][1]); // Range end
			let isInRange = float ? id >= min && id < max && id + 1 >= min && id + 1 < max : id >= min && id < max;

			if (isInRange) {
				return true;
			}
		}

		return false;
	}

	isOverlapping(id, channels, float) {
		let doesOverlap = (id, channel) => {
			let channelId = Number(channel.id);
			let isExistingChannelFloat = channel.type === 'float';
			return isExistingChannelFloat ? id === channelId || id === channelId + 1 : id === channelId;
		};

		for (let i = 0; i < channels.length; i++) {
			return float ? doesOverlap(id, channels[i]) || doesOverlap(id + 1, channels[i]) : doesOverlap(id, channels[i]);
		}

		return false;
	}

	resetId() {
		this.form.controls['id'].setValue('--');
		this.form.markAsDirty();
	}

	controlHasError(controlName: string, validationType: string): boolean {
		return FormHelper.controlHasError(this.form, controlName, validationType);
	}

	submit() {
		if (!this.form.dirty) return;

		const controls = this.form.controls;

		if (this.form.invalid) {
			Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
			return;
		}

		this.form.markAsPristine();
		this.form.markAsUntouched();
		this.form.updateValueAndValidity();

		let newChannel = new ChannelViewModel();
		newChannel.name = controls.name?.value;
		newChannel.description = controls.description?.value;
		newChannel.kind = controls.kind?.value;
		newChannel.id = controls.id?.value;
		newChannel.enabled = controls.enabled?.value;
		newChannel.gain = controls.gain?.value;
		newChannel.min = controls.min?.value;
		newChannel.max = controls.max?.value;
		newChannel.offset = controls.offset?.value;
		newChannel.transform = controls.transform?.value;
		newChannel.type = controls.type?.value === 'int32' ? 'integer' : controls.type?.value;
		newChannel.uom = controls.uom?.value;

		if (this.channelKind === ChannelKindType.TOTALIZER) {
			newChannel.totalizer = new TotalizerViewModel();
			newChannel.totalizer.channelSourceName = controls.channelSourceName?.value;
			newChannel.totalizer.totalizerBase = controls.totalizerBase?.value;
			newChannel.totalizer.totalizerCalcFactor = controls.totalizerCalcFactor?.value;
		} else {
			newChannel.totalizer = null;
		}

		if (this.isEdit) {
			let index = this.unitConfig.channels.findIndex((channel) => channel.name === this.channel?.name);
			this.unitConfig.channels[index] = newChannel;
		} else {
			this.unitConfig.channels.push(newChannel);
		}

		let modifiedUnit = UtilityHelper.deepCopy(this.unitConfig);
		let message: string = '';

		if (this.unitConfig.type === UnitConfigType.MODBUS) {
			// If Modbus
			modifiedUnit = ModbusHelper.transformIds(modifiedUnit, false); // Adds 300k
		}

		this.unitConfigService
			.updateUnitConfig(modifiedUnit)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (c) => {
					this.dialogRef.close(this.unitConfig);
					message = `${this.data.channel ? this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_CAPITAL') }) : this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_CAPITAL') })}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				},
				error: () => {
					if (!this.data.channel) {
						this.unitConfig.channels.pop();
					}
					message = `${this.data.channel ? this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_CAPITAL') }) : this.translateService.instant('GENERIC.NOT_CREATED', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_CAPITAL') })}`;
					this.coreService.toast({ message: message, type: StatusType.DANGER });
				},
			});
	}

	get ChannelKindType() {
		return ChannelKindType;
	}
}

export enum ChannelKindType {
	DIRECT = 1,
	DERIVED = 2,
	TOTALIZER = 3,
}
